import { jsPDF } from 'jspdf';
import { cleanPDFText as cleanPDFTextUtil, cleanMarkdownLine } from './replaceStatements';

// Helper function to clean text throughout PDF
function cleanPDFText(text: string): string {
  return cleanPDFTextUtil(text);
}

export async function generateSummaryPDF(
  summaryContent: string, 
  documentTitle?: string, 
  tableData?: Array<{comment: string, action: string, reviewerComment: string, section: string}>,
  detailedCheckData?: Array<{page: string, subpart: string, score: number, status: string, satisfied: string, missing: string, recommendations: string}>,
  overallComplianceScore?: number
): Promise<void> {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    
    let yPosition = margin;
    doc.setFont('times', 'normal');

    doc.setFontSize(22);
    doc.setFont('times', 'bold');
    const title = cleanPDFText(`Compliance Report`);
    const titleWidth = doc.getTextWidth(title);
    const centerX = (pageWidth - titleWidth) / 2;
    doc.text(title, centerX, yPosition);
    yPosition += 20;

    // Add overall compliance score if provided
    if (overallComplianceScore !== undefined) {
      doc.setFontSize(16);
      doc.setFont('times', 'bold');
      const scoreText = `Overall Compliance Score: ${overallComplianceScore}/100`;
      const scoreWidth = doc.getTextWidth(scoreText);
      const scoreCenterX = (pageWidth - scoreWidth) / 2;
      doc.text(scoreText, scoreCenterX, yPosition);
      yPosition += 15;
    }

    const lines = summaryContent.split('\n');
    
    for (let i = 0; i < lines.length; i++) {
      let line = cleanPDFText(lines[i]);

      if (yPosition > pageHeight - margin - 10) {
        doc.addPage();
        yPosition = margin;
      }
      const sectionHeaderMatch = line.match(/^(Satisfied Requirements|Missing Requirements|Recommendations)\s*(\(\d+ words\))?/i);
      const isOverallScore = line.match(/^OVERALL COMPLIANCE SCORE:/i);
      const isHeading = line.trim().match(/^(Final Compliance Summary|CFR21 Status|Subpart Scores):$/i);
      const isListItem = line.trim().match(/^\d+\./); 
      const isWordCountLine = line.trim().match(/^\(\d+ words\)$/i); 
      const isEmptyOrDash = line.trim() === '' || line.trim() === '--';

      if (isHeading) {
        doc.setFontSize(15);
        doc.setFont('times', 'italic');
        doc.text(line, margin, yPosition);
        yPosition += 12;
      } else if (sectionHeaderMatch) {
        const headerText = sectionHeaderMatch[1];
        let wordCount = sectionHeaderMatch[2] || '';
        
        if (!wordCount && i + 1 < lines.length) {
          const nextLine = lines[i + 1].trim();
          if (nextLine.match(/^\(\d+ words\)$/i)) {
            wordCount = ` ${nextLine}`;
            i++; 
          }
        }
        
        const fullHeader = `${headerText}${wordCount}`;
        doc.setFontSize(14);
        doc.setFont('times', 'bold');
        doc.text(fullHeader, margin, yPosition);
        yPosition += 12;
      } else if (isOverallScore) {
        doc.setFontSize(14);
        doc.setFont('times', 'bold');
        doc.text(line, margin, yPosition);
        yPosition += 12;
      } else if (isWordCountLine || isEmptyOrDash) {
   
        continue;
      } else if (isListItem) {
     
        doc.setFontSize(12);
        doc.setFont('times', 'normal');
        const wrappedLines = doc.splitTextToSize(line, maxWidth);
        for (const wrappedLine of wrappedLines) {
          if (yPosition > pageHeight - margin - 10) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(wrappedLine, margin, yPosition);
          yPosition += 7;
        }
        yPosition += 5;
      } else {
       
        doc.setFontSize(12);
        doc.setFont('times', 'normal');
        const wrappedLines = doc.splitTextToSize(line, maxWidth);
        for (const wrappedLine of wrappedLines) {
          if (yPosition > pageHeight - margin - 10) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(wrappedLine, margin, yPosition);
          yPosition += 7;
        }
      }
    }

    if (tableData && tableData.length > 0) {
      yPosition += 10;
      
      if (yPosition > pageHeight - margin - 100) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(15);
      doc.setFont('times', 'bold');
      doc.text('Action Matrix:', margin, yPosition);
      yPosition += 15;

      // Table dimensions
      const tableWidth = maxWidth;
      const col1Width = tableWidth * 0.6; 
      const col2Width = tableWidth * 0.2; 
      const col3Width = tableWidth * 0.2; 
      const rowHeight = 20;

      doc.setFontSize(12);
      doc.setFont('times', 'bold');
      
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, yPosition, tableWidth, rowHeight, 'F');
      
      doc.setDrawColor(0, 0, 0);
      doc.rect(margin, yPosition, col1Width, rowHeight);
      doc.rect(margin + col1Width, yPosition, col2Width, rowHeight);
      doc.rect(margin + col1Width + col2Width, yPosition, col3Width, rowHeight);
      
      const headerTexts = [
        { text: 'Missing Requirement', x: margin + 5, width: col1Width - 10 },
        { text: 'Action', x: margin + col1Width + 5, width: col2Width - 10 },
        { text: 'Reviewer Comment', x: margin + col1Width + col2Width + 5, width: col3Width - 10 }
      ];
      
      headerTexts.forEach(header => {
        const wrappedHeader = doc.splitTextToSize(header.text, header.width);
        wrappedHeader.forEach((line: string, index: number) => {
          doc.text(line, header.x, yPosition + 12 + (index * 7));
        });
      });
      
      yPosition += rowHeight;

      doc.setFont('times', 'normal');
      
      tableData.forEach(row => {

        const cleanComment = cleanPDFText(row.comment);
        const cleanAction = cleanPDFText(row.action);
        const cleanReviewer = cleanPDFText(row.reviewerComment);
        
        const commentLines = doc.splitTextToSize(cleanComment, col1Width - 10);
        const actionLines = doc.splitTextToSize(cleanAction, col2Width - 10);
        const reviewerLines = doc.splitTextToSize(cleanReviewer, col3Width - 10);
        
        const maxLines = Math.max(commentLines.length, actionLines.length, reviewerLines.length);
        const cellHeight = Math.max(rowHeight, maxLines * 7 + 6);
        
        if (yPosition + cellHeight > pageHeight - margin - 30) {
          doc.addPage();
          yPosition = margin;
        }

        doc.rect(margin, yPosition, col1Width, cellHeight);
        doc.rect(margin + col1Width, yPosition, col2Width, cellHeight);
        doc.rect(margin + col1Width + col2Width, yPosition, col3Width, cellHeight);
        
        let textY = yPosition + 12;
        
        commentLines.forEach((line: string, index: number) => {
          if (index < Math.floor((cellHeight - 6) / 7)) {
            doc.text(line, margin + 5, textY + (index * 7));
          }
        });
        
        actionLines.forEach((line: string, index: number) => {
          if (index < Math.floor((cellHeight - 6) / 7)) {
            doc.text(line, margin + col1Width + 5, textY + (index * 7));
          }
        });
        
        reviewerLines.forEach((line: string, index: number) => {
          if (index < Math.floor((cellHeight - 6) / 7)) {
            doc.text(line, margin + col1Width + col2Width + 5, textY + (index * 7));
          }
        });
        
        yPosition += cellHeight;
      });
    }

    if (detailedCheckData && detailedCheckData.length > 0) {
      yPosition += 20;
      
      if (yPosition > pageHeight - margin - 80) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(18);
      doc.setFont('times', 'bold');
      doc.text('Detailed Analysis', margin, yPosition);
      yPosition += 15;

      detailedCheckData.forEach((check, index) => {
        if (yPosition > pageHeight - margin - 80) {
          doc.addPage();
          yPosition = margin;
        }

        doc.setFontSize(14);
        doc.setFont('times', 'bold');
        doc.text(`Check ${index + 1}: Page ${check.page}, ${check.subpart}`, margin, yPosition);
        yPosition += 10;

        doc.setFontSize(12);
        doc.setFont('times', 'normal');
        doc.text(`Score: ${check.score}% | Status: ${check.status}`, margin, yPosition);
        yPosition += 10;

        if (check.satisfied !== 'N/A') {
          doc.setFont('times', 'bold');
          doc.text('Satisfied Requirements:', margin, yPosition);
          yPosition += 7;
          doc.setFont('times', 'normal');
          
          // Split by bullet points and numbers, convert to numbered list
          const satisfiedText = check.satisfied
            .replace(/- /g, '\n')
            .replace(/(\d+)\./g, '\n')
            .trim();
          
          const satisfiedLines = satisfiedText.split('\n').filter(line => line.trim());
          satisfiedLines.forEach((line: string, index: number) => {
            if (yPosition > pageHeight - margin - 10) {
              doc.addPage();
              yPosition = margin;
            }
            const numberedLine = `${index + 1}. ${line.trim()}`;
            const wrappedLines = doc.splitTextToSize(numberedLine, maxWidth);
            wrappedLines.forEach((wrappedLine: string) => {
              doc.text(wrappedLine, margin, yPosition);
              yPosition += 7;
            });
            yPosition += 3;
          });
          yPosition += 3;
        }

        if (check.missing !== 'N/A') {
          doc.setFont('times', 'bold');
          doc.text('Missing Requirements:', margin, yPosition);
          yPosition += 7;
          doc.setFont('times', 'normal');
          
          // Split by bullet points and numbers, convert to numbered list
          const missingText = check.missing
            .replace(/- /g, '\n')
            .replace(/(\d+)\./g, '\n')
            .trim();
          
          const missingLines = missingText.split('\n').filter(line => line.trim());
          missingLines.forEach((line: string, index: number) => {
            if (yPosition > pageHeight - margin - 10) {
              doc.addPage();
              yPosition = margin;
            }
            const numberedLine = `${index + 1}. ${line.trim()}`;
            const wrappedLines = doc.splitTextToSize(numberedLine, maxWidth);
            wrappedLines.forEach((wrappedLine: string) => {
              doc.text(wrappedLine, margin, yPosition);
              yPosition += 7;
            });
            yPosition += 3;
          });
          yPosition += 3;
        }

        if (check.recommendations !== 'N/A') {
          doc.setFont('times', 'bold');
          doc.text('Recommendations:', margin, yPosition);
          yPosition += 7;
          doc.setFont('times', 'normal');
          
          // Split by bullet points and numbers, convert to numbered list
          const recText = check.recommendations
            .replace(/- /g, '\n')
            .replace(/(\d+)\./g, '\n')
            .trim();
          
          const recLines = recText.split('\n').filter(line => line.trim());
          recLines.forEach((line: string, index: number) => {
            if (yPosition > pageHeight - margin - 10) {
              doc.addPage();
              yPosition = margin;
            }
            const numberedLine = `${index + 1}. ${line.trim()}`;
            const wrappedLines = doc.splitTextToSize(numberedLine, maxWidth);
            wrappedLines.forEach((wrappedLine: string) => {
              doc.text(wrappedLine, margin, yPosition);
              yPosition += 7;
            });
            yPosition += 3;
          });
        }

        yPosition += 10;
      });
    }

    const safeTitle = (documentTitle || 'Document').replace(/[^a-z0-9]/gi, '_');
    const dateStamp = new Date().toISOString().split('T')[0];
    const filename = `${safeTitle}_Analysis_Summary_${dateStamp}.pdf`;

    doc.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF summary');
  }
}